import { useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import WeeklyCalendar from "@/components/WeeklyCalendar";
import MonthCalendar from "@/components/MonthCalendar";
import { CalendarDays, LayoutGrid } from "lucide-react";

export default function CalendarView({ role, clients, onChanged }) {
  const [sp, setSp] = useSearchParams();
  const view = sp.get("view") === "month" ? "month" : "week";
  const focusDate = sp.get("date") || null;
  const [refreshKey, setRefreshKey] = useState(0);

  const setView = useCallback((v) => {
    setSp(prev => {
      const n = new URLSearchParams(prev);
      n.set("view", v);
      return n;
    }, { replace: true });
  }, [setSp]);

  const setFocusDate = useCallback((d) => {
    setSp(prev => {
      const n = new URLSearchParams(prev);
      if (d) n.set("date", d); else n.delete("date");
      return n;
    }, { replace: true });
  }, [setSp]);

  // Both params in ONE setSp so React Router applies them atomically —
  // avoids the second setter clobbering the first when a day is clicked in month view.
  const setDateAndView = useCallback((d, v) => {
    setSp(prev => {
      const n = new URLSearchParams(prev);
      if (d) n.set("date", d); else n.delete("date");
      n.set("view", v);
      return n;
    }, { replace: true });
  }, [setSp]);

  const handleDayClick = useCallback((dateStr) => { setDateAndView(dateStr, "week"); }, [setDateAndView]);
  const handleSlotClick = useCallback((slot) => { setDateAndView(slot.date, "week"); }, [setDateAndView]);
  const handleMonthChange = useCallback((ym) => { setFocusDate(ym); }, [setFocusDate]);
  const handleWeekChange = useCallback((monday) => { setFocusDate(monday); }, [setFocusDate]);
  const bump = useCallback(() => { setRefreshKey(k => k + 1); onChanged?.(); }, [onChanged]);

  return (
    <div>
      <div className="flex items-center justify-end mb-4">
        <div className="inline-flex border border-white/10" role="tablist">
          <button
            data-testid="view-week"
            onClick={() => setView("week")}
            className={`px-4 py-2 flex items-center gap-2 font-heading uppercase tracking-widest text-xs transition-colors ${view === "week" ? "bg-[#A855F7] text-white" : "text-white/60 hover:text-white"}`}
          >
            <CalendarDays size={14} /> Settimana
          </button>
          <button
            data-testid="view-month"
            onClick={() => setView("month")}
            className={`px-4 py-2 flex items-center gap-2 font-heading uppercase tracking-widest text-xs transition-colors border-l border-white/10 ${view === "month" ? "bg-[#A855F7] text-white" : "text-white/60 hover:text-white"}`}
          >
            <LayoutGrid size={14} /> Mese
          </button>
        </div>
      </div>

      {view === "week" ? (
        <WeeklyCalendar
          key={focusDate || "default"}
          role={role}
          clients={clients}
          onChanged={bump}
          initialDate={focusDate}
          onWeekChange={handleWeekChange}
        />
      ) : (
        <MonthCalendar
          role={role}
          initialYearMonth={focusDate}
          onDayClick={handleDayClick}
          onSlotClick={handleSlotClick}
          onMonthChange={handleMonthChange}
          refreshKey={refreshKey}
        />
      )}
    </div>
  );
}
